import { Link } from "react-router-dom";
import { ArrowRight, Check, CircleDashed, Telescope } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const ROADMAP = [
    {
        icon: Check,
        tag: "Current",
        title: "Lyanka AI",
        text: "AI Image & Video Creation Platform — the company's first major technology product.",
        active: true,
    },
    {
        icon: CircleDashed,
        tag: "Next",
        title: "New AI & Digital Products",
        text: "Additional applications and digital platforms across AI and emerging technology categories.",
        status: "Under Development / Planned",
    },
    {
        icon: Telescope,
        tag: "Future",
        title: "AI Entertainment & Streaming",
        text: "Long-term product direction — an AI-focused digital entertainment platform for AI-generated films and cinematic content.",
    },
];

export default function Vision() {
    return (
        <div data-testid="vision-page">
            <SEO
                title="Vision & Roadmap | ANA International Private Limited"
                description="ANA International Private Limited is building the next generation of digital products across artificial intelligence, creativity and digital entertainment — starting with Lyanka AI."
                path="/vision"
            />

            <section className="relative pt-40 pb-24 overflow-hidden">
                <div className="absolute inset-0 grid-bg" />
                <div className="absolute top-[-20%] left-[-10%] w-[700px] h-[700px] glow-gold rounded-full" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
                    <Reveal>
                        <Eyebrow>Vision & Roadmap</Eyebrow>
                        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-tight max-w-4xl">
                            Building the Next Generation of <span className="gold-text">Digital Products.</span>
                        </h1>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-8 max-w-3xl" data-testid="vision-intro">
                            ANA INTERNATIONAL PRIVATE LIMITED is building a portfolio of technology products focused on
                            the rapidly evolving intersection of artificial intelligence, creativity and digital
                            entertainment. The company aims to develop products that simplify complex technologies and
                            make them accessible through intuitive digital platforms.
                        </p>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-5 max-w-3xl">
                            The long-term vision is to build multiple proprietary applications rather than depend on a
                            single product.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ROADMAP */}
            <section className="py-24 bg-[#0d0d0f] border-y border-white/[0.07]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Reveal>
                        <Eyebrow>What's Next</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5 max-w-2xl leading-tight">
                            A product roadmap built for the long term
                        </h2>
                        <p className="text-[#a3a29b] text-base leading-relaxed mt-6 max-w-3xl">
                            ANA INTERNATIONAL plans to develop additional applications and digital platforms in the
                            future. Future products under consideration include an AI-focused digital entertainment
                            platform where users could discover and experience AI-generated films and cinematic content
                            through a subscription-based streaming experience. Additional AI and digital products may be
                            introduced as the company expands its technology portfolio.
                        </p>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-5 mt-14" data-testid="roadmap-cards">
                        {ROADMAP.map((r, i) => (
                            <Reveal key={r.tag} delay={i * 0.1}>
                                <div className={`rounded-3xl p-9 h-full flex flex-col ${r.active ? "border border-[#c9a227]/40 bg-gradient-to-b from-[#c9a227]/[0.08] to-transparent" : "card-line"}`}>
                                    <div className="flex items-center justify-between">
                                        <r.icon size={24} className="text-[#e8c766]" />
                                        <span className="mono-label !text-[10px]">{r.tag}</span>
                                    </div>
                                    <h3 className="font-display font-semibold text-xl mt-7">{r.title}</h3>
                                    <p className="text-sm text-[#a3a29b] leading-relaxed mt-4 flex-1">{r.text}</p>
                                    {r.status && (
                                        <p className="text-xs font-mono tracking-widest uppercase text-[#c9a227] mt-6">{r.status}</p>
                                    )}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
                    <Reveal>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl max-w-3xl mx-auto leading-tight">
                            Follow our journey as the portfolio <span className="gold-text">grows.</span>
                        </h2>
                        <div className="flex flex-wrap gap-4 mt-10 justify-center">
                            <Link to="/lyanka-ai" className="btn-gold" data-testid="vision-cta-lyanka">
                                Explore Lyanka AI <ArrowRight size={16} />
                            </Link>
                            <Link to="/contact" className="btn-line" data-testid="vision-cta-contact">
                                Get in Touch
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
